<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Edit / View Paging Group</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation" :disabled="numUsed"
            >Save</el-button
          >
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          />
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Paging Group" :record="selectedPagingGroup" />
      <div class="inline-form-group">
        <el-col :span="11">
          <el-input v-model="number" placeholder="Enter number">
            <template slot="prefix"><span>Number</span></template>
            <template slot="append" v-if="numUsed"
              ><in-use :inUse="numInUse"
            /></template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="12">
          <el-input v-model="editForm.name" placeholder="Enter name">
            <template slot="prefix"><span>Name</span></template>
          </el-input>
        </el-col>
      </div>

      <div class="inline-form-group">
        <el-checkbox v-model="editForm.twoWayAudio"
          >Enable two way audio</el-checkbox
        >
      </div>

      <div class="inline-form-group">
        <el-checkbox v-model="editForm.checkInUse"
          >Enable checking if extension is in use</el-checkbox
        >
      </div>

      <h2 class="form-section-header">
        Assign extensions
      </h2>
      <el-select
        v-model="editForm.extensions"
        placeholder="Select extensions"
        remote
        :remote-method="remoteMethodForExts"
        filterable
        multiple
      >
        <template slot="prefix">
          <span style="opacity: 0;">Extensions</span>
        </template>
        <el-option
          v-for="(ext, idx) in extensionOptions"
          :key="idx + getRandomKey()"
          :label="`${ext.name}${ext.number ? '-' + ext.number : ''}`"
          :value="ext.id"
        />
      </el-select>

      <!-- ⭐ Play Intro Message -->
      <div class="inline-form-group">
        <el-col :span="24" class="mt-10">
          <existing-mediafile
            title="Play intro message"
            :multiple="false"
            :clearState="clearFormFields"
            :newMediafile="newMediafile"
            :selectedMeId="editForm.meId"
            @mediafileSelected="onPlayIntroMsgSelected"
          />
          <new-mediafile @mediafileSaved="onNewMediafileCreation" />
        </el-col>
      </div>
    </el-form>
  </el-dialog>
</template>

<script>
import ExistingMediafile from "@/components/ExistingMediafile";
import NewMediafile from "@/components/NewMediafile";
import RecentAudits from "@/components/RecentAudits";
import InUse from "@/components/InUse";

export default {
  name: "PagingGroupeditForm",

  props: {
    reRenderKey: { required: true },
    selectedPagingGroup: { required: true },
    editVisible: { required: true },
    editProcessing: { required: true },
    extOptions: { required: true },
    numInUse: { required: true },
    clearFormFields: { required: true }
  },

  components: {
    ExistingMediafile,
    NewMediafile,
    RecentAudits,
    InUse
  },

  data() {
    return {
      newMediafile: null,
      number: "",
      initialNumber: "",
      isNumberChangedByUser: false,
      editForm: {
        tenantId: null,
        number: "",
        name: "",
        twoWayAudio: false,
        checkInUse: false,
        extensions: [],
        meId: 0
      },
      extensionOptions: []
    };
  },

  computed: {
    numUsed() {
      return this.numInUse && this.numInUse.inUse;
    }
  },

  watch: {
    extOptions: {
      immediate: true,
      handler(val) {
        if (val && val.length) {
          this.extensionOptions = [...val];
        }
      }
    },

    number(val) {
      if (val) {
        if (this.isNumberChangedByUser && this.initialNumber !== val) {
          this.$emit("checkForAvailableNum", val);
        } else {
          this.isNumberChangedByUser = true;
          this.initialNumber = val;
        }
        this.editForm.number = val;
      }
    },

    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    selectedPagingGroup(val) {
      const {
        tenantId,
        number,
        name,
        twoWayAudio,
        checkInUse,
        extensions,
        meId
      } = val;
      this.editForm.tenantId = tenantId;
      this.number = number;
      this.editForm.name = name;
      this.editForm.twoWayAudio = twoWayAudio === "yes";
      this.editForm.checkInUse = checkInUse === "on";
      this.editForm.meId = meId;

      if (extensions && extensions.length) {
        this.editForm.extensions = extensions.map(item => item.id);
        const extOptionIds = this.extensionOptions.map(item => item.id);
        const extToAdd = extensions.filter(
          item => !extOptionIds.includes(item.id)
        );
        this.extensionOptions = [...this.extensionOptions, ...extToAdd];
      }
    }
  },

  methods: {
    onNewMediafileCreation(newMediafile) {
      this.newMediafile = newMediafile;
    },

    onPlayIntroMsgSelected(meId) {
      this.editForm.meId = meId;
    },

    remoteMethodForExts(query) {
      this.$emit("remoteMethodForExts", query);
    },

    editOperation() {
      const editBody = { ...this.editForm };
      editBody.meId = editBody.meId || 0;
      editBody.twoWayAudio = editBody.twoWayAudio ? "yes" : "no";
      editBody.checkInUse = editBody.checkInUse ? "on" : "";

      this.$emit("editOperation", editBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    getRandomKey() {
      const randomString = () =>
        Math.random()
          .toString(36)
          .slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },

    clearFields() {
      this.number = "";
      this.isNumberChangedByUser = false;
      this.newMediafile = null;
      this.editForm = {
        tenantId: null,
        number: "",
        name: "",
        twoWayAudio: false,
        checkInUse: false,
        extensions: [],
        meId: 0
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.el-checkbox {
  margin-left: 0;
}
</style>
